import { createContext, useContext, useEffect, useMemo, useState } from "react";

const PLATFORM_STORAGE_KEY = "lotq_platform_mode";
const VALID_MODES = new Set(["web", "app"]);

const PlatformContext = createContext(null);

function normalizeMode(value) {
  return VALID_MODES.has(value) ? value : null;
}

function resolveInitialMode() {
  const envMode = normalizeMode(process.env.REACT_APP_PLATFORM);

  if (typeof window === "undefined") {
    return envMode || "web";
  }

  const params = new URLSearchParams(window.location.search);
  const queryMode = normalizeMode(params.get("platform"));
  if (queryMode) {
    window.localStorage.setItem(PLATFORM_STORAGE_KEY, queryMode);
    return queryMode;
  }

  const storedMode = normalizeMode(window.localStorage.getItem(PLATFORM_STORAGE_KEY));
  // A browser can retain the app mode after opening a platform=app URL. The
  // native build has REACT_APP_PLATFORM=app, so an app mode stored in a web
  // build should never override the web shell.
  if (storedMode === "app" && envMode !== "app") {
    window.localStorage.removeItem(PLATFORM_STORAGE_KEY);
    return envMode || "web";
  }
  if (storedMode) {
    return storedMode;
  }

  return envMode || "web";
}

export function PlatformProvider({ children }) {
  const [mode, setModeState] = useState(resolveInitialMode);

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.dataset.platform = mode;
      document.body.dataset.platform = mode;
    }
  }, [mode]);

  const setMode = (nextMode) => {
    const normalized = normalizeMode(nextMode);
    if (!normalized) return;

    setModeState(normalized);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(PLATFORM_STORAGE_KEY, normalized);
    }
  };

  const value = useMemo(() => ({
    mode,
    isApp: mode === "app",
    isWeb: mode === "web",
    setMode,
  }), [mode]);

  return (
    <PlatformContext.Provider value={value}>
      {children}
    </PlatformContext.Provider>
  );
}

export function usePlatform() {
  const context = useContext(PlatformContext);
  if (!context) {
    throw new Error("usePlatform must be used within PlatformProvider");
  }
  return context;
}
